import React, { useEffect, useRef } from 'react';
import CompanyReviewScript from '@v2/shared/components/CompanyReviewScript';
import ResellerRatingsBadge from '@v2/shared/components/ResellerRatingsBadge';
import useIntersectionObserver from '@v2/shared/lib/useIntersectionObserver';

/**
 * How far ahead of the footer the loader is fetched — the same margin, and the same
 * reasoning, as /guarantee's ReviewShowcase.
 */
const ROOT_MARGIN = '400px';

/** The seal beside this one opens at 780x760; two review popups should not differ. */
const POPUP_FEATURES = 'height=760,width=780,scrollbars=1,noopener';

/**
 * Reopens the badge's own links in the popup the seal above it uses.
 *
 * This is delegation, and it has to be: the anchors belong to the vendor's script, so
 * the listener sits on *our* container and reads the anchor at click time. Nothing of
 * theirs is wrapped, rewritten or replaced — the href, the `target`, the `rel` and the
 * `aria-label` are all still the ones they shipped, which is what keeps the "leave a
 * review widget's links alone" rule intact. It also survives the vendor re-rendering,
 * which reaching in to rebind a handler on their anchor would not.
 *
 * Opening `link.href` rather than a url written here is the same point: the badge ships
 * *two* anchors — the rating, and their logo, which goes to the site root — so a literal
 * url would send one of them somewhere it was never pointed.
 *
 * Verified against their live widget before this was written, because none of it is safe
 * to assume: they attach no handler of their own and do not stop propagation, and they
 * navigate by the anchor's default rather than calling `window.open` themselves — so
 * `preventDefault` genuinely suppresses the tab. Had they opened it in script, this would
 * have produced *both* windows and there would have been nothing to do about it.
 *
 * Capture phase so it still holds if they add a handler that stops propagation later.
 *
 * What deliberately keeps the browser's own behaviour is a modified click: ctrl/cmd/shift
 * -click is the visitor asking for a tab or window of their own, and a popup is not an
 * answer to that. Middle click needs no guard — it fires `auxclick`, not `click` — and a
 * right-click "open in new tab" fires neither, which is the other half of why the
 * vendor's real href has to stay on the element rather than being moved into script.
 *
 * `noopener` costs the named window's reuse: the browser ignores the name, so two clicks
 * make two windows instead of reusing one. Taken deliberately, and it is the one place
 * this differs from the ResellerRatings seal. The vendor sets `rel="nofollow noopener"`
 * on these anchors themselves, and quietly dropping a vendor's security attribute while
 * changing the shape of their window is the worse of the two trades.
 */
function openInPopup(event: MouseEvent) {
  const target = event.target;

  if (event.defaultPrevented || event.button !== 0) {
    return;
  }

  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  if (!(target instanceof Element)) {
    return;
  }

  const link = target.closest('a[href]');

  // Only a real navigation belongs in a window of its own; a `mailto:` or a `#` anchor
  // would be actively broken by one.
  if (!(link instanceof HTMLAnchorElement) || !/^https?:$/.test(link.protocol)) {
    return;
  }

  event.preventDefault();
  window.open(link.href, 'smartcustomer', POPUP_FEATURES);
}

/**
 * The two review seals in the footer's trust column.
 *
 * **Both keep the link they arrive with, and this column adds none.** ResellerRatings is
 * `shared/components/ResellerRatingsBadge` — the widget the footer has always carried,
 * which intercepts the click and reopens the seal in the sized popup their landing script
 * expects. SmartCustomer's badge injects its own anchor to
 * `smartcustomer.com/reviews/discountmags.com`, already `target="_blank"` and
 * `rel="nofollow noopener"`. Neither is wrapped, covered or made `inert`: a seal is a
 * claim the vendor is willing to be checked on, so the click has to reach the vendor.
 * /reviews, which carries both providers' reviews, is a row of its own in the Company
 * column.
 *
 * **Both open in the same sized popup, and that is a change of window, not of
 * destination.** Asana 1218560764316485 asked for the badge to match the seal above it.
 * Redirecting a seal at one of our own pages is still the thing not to do — what moves
 * here is only which window the vendor's own url opens in. See `openInPopup`.
 *
 * **Neither carries a number of its own.** Each vendor draws its own score into its own
 * seal, so a rating or review count written into this markup would be a second, frozen
 * copy of a figure that moves — and it would sit right beside the live one.
 *
 * The SmartCustomer badge is the `box` variant of the same widget family as the homepage
 * carousel and /guarantee's showcase: one loader, one account, and the container class
 * picks the widget. So this mounts `CompanyReviewScript` rather than naming the script
 * url — a second loader would fetch the vendor twice on both of those pages.
 *
 * **The loader is gated on the footer being in view, and that is not only about cost.**
 * A footer is on every page and below every one of them, so an ungated loader would fetch
 * a third-party script on every page view for a badge most visitors never scroll to. It
 * would also break `/guarantee`: `CompanyReviewScript` de-duplicates on the script tag
 * already being in the document, so a footer that loads it at first paint makes
 * ReviewShowcase's own intersection gate a no-op, silently undoing the deferral that
 * component is built around. Gating here leaves whichever of the two is reached first to
 * do the fetching. Mounting from an effect also gets the ordering right on a legacy page,
 * where the footer is client-rendered: the badge is committed before the script is
 * appended, so the widget always finds its container. A `<script>` written into the
 * template before `</body>` would run first and find nothing.
 */
export default function TrustSeals() {
  const [ref, isInView] = useIntersectionObserver({ rootMargin: ROOT_MARGIN });
  const badge = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = badge.current;

    if (!container) {
      return;
    }

    container.addEventListener('click', openInPopup, true);

    return () => container.removeEventListener('click', openInPopup, true);
  }, []);

  return (
    <div ref={ref} className="flex flex-col items-start gap-3">
      {isInView ? <CompanyReviewScript /> : null}

      <ResellerRatingsBadge />

      {/* The badge paints itself in its own dark grey — it writes
          `--sj-foreground: #464646` onto this element — so it is configured for a light
          surface, and on the footer's near-black the rating is unreadable without one.

          `empty:hidden` is the blocked-vendor case. The widget fills this div, and ad
          blockers and tracking protection block that script routinely; unhidden, a blocked
          badge is a bare 200x16 white sliver of padding sitting in the column. The widget
          fills it while it is still hidden — it renders on the loader firing and does not
          consult the container's visibility — so the two do not deadlock. */}
      <div
        ref={badge}
        className="stjr-badge w-[200px] max-w-full rounded-md bg-white px-3 py-2 empty:hidden"
        data-version="box"
      />
    </div>
  );
}
